import CryptoJS from 'crypto-js';

// Utils

import { cloneDeep, differenceWith, isEqual } from '../miscellaneous/objectHelpers';
import { cloneObject } from '../miscellaneous/cloneObject';
import { getCurrentAndMaxAchievedPages } from '@utils/miscellaneous/getCurrentAndMaxAchievedPages';
import { householdMemberTools } from '@utils/tools/householdMemberTools';
import { maskValue } from '../miscellaneous/mask.utils';
import { processJsonTools } from '@utils/tools/processJsonTools';
import { processUiProperties } from '@utils/tools/processUiProperties';
import { familyAnalyzer } from '../householdMember/familyAnalyzer';
import { questionsVisibility } from '../miscellaneous/pageAndQuestionsVisibility';
import { socialSecurityNumberTools } from '../tools/socialSecurityNumberTools';
import { updateDirtyFlags } from '@utils/miscellaneous/updateDirtyFlags';
import { updateImiValues, updateSsnValues } from '@utils/householdMember/updateMaskedValues';

// Constants

import { EMPTY_OBJECTS } from '@constants/ssap/initialValues/emptyObjects';
import { PAGES } from '@constants/ssap/flows/pagesTypes';
import { lotNumberTools } from '@utils/tools/lotNumberTools';

// Types

import { GenderIdentification, SexualOrientation } from '@components/ssap/SsapTypes';

export const processInPipeline = (...fns) => (...args) => {
    const [ params, defaultReturnValue ] = args;

    return fns.reduce(
        (acc, fn) => {
            return fn(
                {
                    ...params,
                    dataAfterChanges: cloneDeep(acc.cleanedData)
                },
                acc
            );
        },
        defaultReturnValue
    );
};

const performCleanUpAndChecksForEthnicityAndRace = (...args) => {
    const [ params, defaultReturnValue ] = args;

    const {
        dataAfterChanges,
        dataBeforeChanges,
        isSogiFeatureEnabled,
        serverDate
    } = params;

    // We are getting arrays of booleans for each household member
    // "true" if we are going to render SOGI questions for this HHM on ETHNICITY_AND_RACE_PAGE
    // "false" if we are not
    const [
        hhmVisibilityBeforeChanges,
        hhmVisibilityAfterChanges
    ] = [ dataBeforeChanges, dataAfterChanges ].map(data => {
        return data.taxHousehold.householdMember.map(hhm => {
            return questionsVisibility.isSogiShown({
                hhm,
                isSogiFeatureEnabled,
                serverDate
            });
        });
    });

    const areChanges = !isEqual(hhmVisibilityBeforeChanges, hhmVisibilityAfterChanges);

    let cleanedData = defaultReturnValue.cleanedData,
        shouldRestrictNavigation = defaultReturnValue.shouldRestrictNavigation;

    if(areChanges) {
        hhmVisibilityAfterChanges.forEach((isVisible, index) => {
            if(isVisible === false) {
                // Since HHM won't be rendered on ETHNICITY_AND_RACE_PAGE
                // then we need to clean up all the data related to this HHM
                cleanedData.taxHousehold.householdMember[index] = {
                    ...cleanedData.taxHousehold.householdMember[index],
                    genderIdentification: GenderIdentification.UNSPECIFIED,
                    sexualOrientation: SexualOrientation.UNSPECIFIED
                };
            } else {
                // We do nothing here
            }
        });
    }

    // We are not restricting navigation because Date of Birth input is non-standalone one
    // Restriction will be made by input
    return {
        cleanedData,
        shouldRestrictNavigation
    };
};

const performCleanUpAndChecksForMedicaidTermination = (...args) => {
    const [ params, defaultReturnValue ] = args;

    const {
        dataAfterChanges,
        dataBeforeChanges,
        medicaidTerminationFeatureEnabled,
        serverDate,
        medicaidEndDateOverride
    } = params;

    // We are getting arrays of booleans for each household member
    // "true" if we are going to render questions for this HHM on MEDICAID_CHIP_DENIAL_INFORMATION_PAGE
    // "false" if we are not
    const [
        hhmVisibilityBeforeChanges,
        hhmVisibilityAfterChanges
    ] = [ dataBeforeChanges, dataAfterChanges ].map(data => {
        return data.taxHousehold.householdMember.map(hhm => {
            return questionsVisibility.isMedicaidChipDenialShown({
                hhm,
                medicaidTerminationFeatureEnabled,
                serverDate,
                medicaidEndDateOverride
            });
        });
    });

    const areChanges = !isEqual(hhmVisibilityBeforeChanges, hhmVisibilityAfterChanges);

    let cleanedData = defaultReturnValue.cleanedData,
        shouldRestrictNavigation = defaultReturnValue.shouldRestrictNavigation;

    if(areChanges) {
        hhmVisibilityAfterChanges.forEach((isVisible, index) => {
            if(isVisible === false) {
                // Since HHM won't be rendered on MEDICAID_CHIP_DENIAL_INFORMATION_PAGE
                // then we need to clean up all the data related to this HHM
                cleanedData.taxHousehold.householdMember[index] = {
                    ...cleanedData.taxHousehold.householdMember[index],
                    ...cloneObject(EMPTY_OBJECTS.MEDICAID_CHIP_DENIAL)
                };
            } else {
                // If we track that HHM will be rendered on MEDICAID_CHIP_DENIAL_INFORMATION_PAGE
                // but in the data before changes this HHM was not rendered
                // then we need to restrict navigation and set maxAchievedPage to MEDICAID_CHIP_DENIAL_INFORMATION_PAGE
                if(shouldRestrictNavigation === false && hhmVisibilityBeforeChanges[index] === false) {
                    shouldRestrictNavigation = true;
                }
            }
        });
    }

    return {
        cleanedData,
        shouldRestrictNavigation
    };
};


const performHomeAddressSyncForHhmsWhoLiveTogetherWithPcp = (...args) => {
    const [ _, defaultReturnValue ] = args;

    let cleanedData = defaultReturnValue.cleanedData,
        shouldRestrictNavigation = defaultReturnValue.shouldRestrictNavigation;

    const hhms = cleanedData.taxHousehold.householdMember;
    const pcpHomeAddress = householdMemberTools.getPrimaryContact(hhms).householdContact.homeAddress;

    householdMemberTools.updatePrimaryContact(
        hhms,
        {
            householdContact: {
                homeAddress: pcpHomeAddress
            }
        },
        false,
        [
            {
                checkValues: [ false, null ],
                checkKey: 'livesAtOtherAddressIndicator',
                syncKey: 'householdContact.homeAddress'
            }
        ]
    );

    return {
        cleanedData,
        shouldRestrictNavigation
    };
};

// Safe At Home participants see "Lot Number" as the streetAddress1 label/placeholder and
// typically type just the number itself (e.g. "12345"). We add the "Lot Number " prefix
// here, at save time, so the UI is left untouched while the persisted value is unambiguous.

const performLotNumberPrefixForConfidentialAddress = (...args) => {
    const [ _, defaultReturnValue ] = args;

    let cleanedData = defaultReturnValue.cleanedData,
        shouldRestrictNavigation = defaultReturnValue.shouldRestrictNavigation;

    if(cleanedData.addressConfidentialityParticipant) {
        const hhms = cleanedData.taxHousehold.householdMember;
        const primaryContact = householdMemberTools.getPrimaryContact(hhms);
        const { streetAddress1 } = primaryContact.householdContact.mailingAddress;
        const prefixedStreetAddress1 = lotNumberTools.addLotNumberPrefix(streetAddress1);

        if(prefixedStreetAddress1 !== streetAddress1) {
            cleanedData = {
                ...cleanedData,
                taxHousehold: {
                    ...cleanedData.taxHousehold,
                    householdMember: householdMemberTools.updatePrimaryContact(hhms, {
                        householdContact: {
                            ...primaryContact.householdContact,
                            mailingAddress: {
                                ...primaryContact.householdContact.mailingAddress,
                                streetAddress1: prefixedStreetAddress1
                            }
                        }
                    })
                }
            };
        }
    }

    return {
        cleanedData,
        shouldRestrictNavigation
    };
};

const performCleanUpAndChecksForHouseholdAddresses = (...args) => {
    const [params, defaultReturnValue] = args;

    const {
        dataAfterChanges,
        dataBeforeChanges,
        additionalResidencyEnabled
    } = params;

    // We are getting arrays of states for each household member in previous and current data
    const [
        hhmVisibilityBeforeChanges,
        hhmVisibilityAfterChanges
    ] = [dataBeforeChanges, dataAfterChanges].map(data => {
        return data.taxHousehold.householdMember.map(hhm => {
            return hhm.householdContact.homeAddress.state;
        });
    });

    const areChanges = !isEqual(hhmVisibilityBeforeChanges, hhmVisibilityAfterChanges);

    // Restricting navigation only if changes found in state in address updates
    return {
        ...defaultReturnValue,
        shouldRestrictNavigation: areChanges && additionalResidencyEnabled
    };
};

/**
 * Clears ESC or HRA data for any household member for whom the respective page would not be shown.
 * Uses isESCorHRAShown with checkHRA flag: checkHRA true = HRA page (clear healthReimbursementArrangement),
 * checkHRA false = ESC page (clear employerSponsoredCoverage). Keeps saved data in sync with page
 * visibility so child data is not persisted when parent removes attestation.
 */
const performCleanUpAndChecksForEscOrHra = (params, defaultReturnValue) => {
    const {
        bloodRelationshipCodes,
        dataAfterChanges,
        inlineCurrentIncomeEnabled,
        inlineEligibilityIgnoredForEscPage,
        isState = {},
        loadedRelations,
        pageName,
        serverDate
    } = params;

    const householdMembers = dataAfterChanges?.taxHousehold?.householdMember;
    if (!Array.isArray(householdMembers) || householdMembers.length === 0 || bloodRelationshipCodes == null || loadedRelations == null) {
        return defaultReturnValue;
    }

    const bloodRelationships = dataAfterChanges?.taxHousehold?.bloodRelationship ?? [];
    const primaryTaxFilerPersonId = dataAfterChanges?.taxHousehold?.primaryTaxFilerPersonId;

    const familyConfigBase = {
        bloodRelationships,
        bloodRelationshipCodes,
        householdMembers,
        isState,
        loadedRelations,
        primaryTaxFilerPersonId,
        serverDate
    };

    const dataKey = pageName === PAGES.HEALTH_REIMBURSEMENT_ARRANGEMENT_DETAIL_PAGE ? 'healthReimbursementArrangement' : 'employerSponsoredCoverage';

    let cleanedData = defaultReturnValue.cleanedData;
    const members = cleanedData.taxHousehold.householdMember;

    cleanedData = {
        ...cleanedData,
        taxHousehold: {
            ...cleanedData.taxHousehold,
            householdMember: members.map((hhm, idx) => {
                if ((hhm[dataKey]?.length ?? 0) === 0) {
                    return hhm;
                }
                const hhmStatus = familyAnalyzer({ ...familyConfigBase, currentMemberIndex: idx });
                const currentOtherInsurance = hhm.healthCoverage?.currentOtherInsurance ?? {};
                const isPageApplicableForChild = questionsVisibility.isESCorHRAShown({
                    checkHRA: pageName === PAGES.HEALTH_REIMBURSEMENT_ARRANGEMENT_DETAIL_PAGE,
                    currentOtherInsurance,
                    hhm,
                    hhmStatus,
                    inlineCurrentIncomeEnabled,
                    inlineEligibilityIgnoredForEscPage,
                    preliminaryAptcCsrStatus: hhm.inlineEligibility?.preliminaryAptcCsrStatus,
                    serverDate
                });
                if (!isPageApplicableForChild) {
                    const indicatorKey = pageName === PAGES.HEALTH_REIMBURSEMENT_ARRANGEMENT_DETAIL_PAGE
                        ? 'offeredIchraIndicator'
                        : 'employerWillOfferInsuranceIndicator';
                    return {
                        ...hhm,
                        [dataKey]: [],
                        healthCoverage: {
                            ...(hhm.healthCoverage || {}),
                            [indicatorKey]: null
                        }
                    };
                }

                return hhm;
            })
        }
    };

    return {
        ...defaultReturnValue,
        cleanedData
    };
};

export const performCleanUpAndChecks = params => {

    const {
        dataAfterChanges,
        pageName,
    } = params;

    const defaultReturnValue = {
        cleanedData: cloneDeep(dataAfterChanges),
        shouldRestrictNavigation: false
    };

    const payload = [ params, defaultReturnValue ];

    switch(pageName) {
        case PAGES.OTHER_ADDRESSES_PAGE:
            return performCleanUpAndChecksForHouseholdAddresses(...payload);
        case PAGES.ABOUT_YOUR_HOUSEHOLD_PAGE:
            return performCleanUpAndChecksForEthnicityAndRace(...payload);
        case PAGES.PRIMARY_CONTACT_INFORMATION_PAGE:
            return processInPipeline(
                performHomeAddressSyncForHhmsWhoLiveTogetherWithPcp,
                performLotNumberPrefixForConfidentialAddress,
                performCleanUpAndChecksForEthnicityAndRace
            )(...payload);
        case PAGES.MEDICAID_TERMINATION_DETAILS_PAGE:
            return performCleanUpAndChecksForMedicaidTermination(...payload);
        case PAGES.HEALTH_REIMBURSEMENT_ARRANGEMENT_DETAIL_PAGE:
        case PAGES.EMPLOYER_COVERAGE_DETAIL_PAGE:
            return performCleanUpAndChecksForEscOrHra(...payload);
        default:
            return defaultReturnValue;
    }
};

export const getApplicantGuids = data => {
    return data.taxHousehold.householdMember.map(hhm => ({
        applicantGuid: hhm.applicantGuid
    }));
};

const getSSNs = data => {
    return data.taxHousehold.householdMember.map(hhm => ({
        applicantGuid: hhm.applicantGuid,
        ssn: hhm.socialSecurityCard.socialSecurityNumber
    }));
};

const getIMIdata = data => {
    return data.taxHousehold.householdMember.map(hhm => {
        const {
            applicantGuid,
            citizenshipImmigrationStatus: {
                citizenshipDocument: { alienNumber, foreignPassportOrDocumentNumber, i94Number, sevisId, visaNumber } = {}
            } = {}
        } = hhm ?? {};

        return ({ applicantGuid, alienNumber, foreignPassportOrDocumentNumber, i94Number, sevisId, visaNumber });
    });
};

export const maskPII = data => {
    const returnData = {
        ...data,
        taxHousehold: {
            ...data.taxHousehold,
            householdMember: data.taxHousehold.householdMember.map(hhm => ({
                ...hhm,
                socialSecurityCard: {
                    ...hhm.socialSecurityCard,
                    socialSecurityNumber: socialSecurityNumberTools.maskSsn(hhm.socialSecurityCard.socialSecurityNumber, true)
                },
                citizenshipImmigrationStatus: {
                    ...hhm.citizenshipImmigrationStatus,
                    citizenshipDocument: {
                        ...hhm.citizenshipImmigrationStatus.citizenshipDocument,
                        alienNumber: maskValue(hhm.citizenshipImmigrationStatus.citizenshipDocument.alienNumber),
                        foreignPassportOrDocumentNumber: maskValue(hhm.citizenshipImmigrationStatus.citizenshipDocument.foreignPassportOrDocumentNumber),
                        i94Number: maskValue(hhm.citizenshipImmigrationStatus.citizenshipDocument.i94Number),
                        sevisId: maskValue(hhm.citizenshipImmigrationStatus.citizenshipDocument.sevisId),
                        visaNumber: maskValue(hhm.citizenshipImmigrationStatus.citizenshipDocument.visaNumber)
                    }

                }
            }))
        }
    };
    return returnData;
};

export const getPayloadForSsnUpdate = (data, localState, pageName) => {
    if (pageName === PAGES.PERSONAL_INFORMATION_PAGE) {
        const [diff] = differenceWith(getSSNs(data), getSSNs(localState.frozenData), isEqual);
        return { ssnToUpdate: diff ? diff : null };
    } else {
        return { ssnToUpdate: null };
    }
};

export const getPayloadForIMIUpdate = (data, localState, pageName) => {
    if (pageName === PAGES.CITIZENSHIP_IMMIGRATION_STATUS_PAGE) {
        const [diff] = differenceWith(getIMIdata(data), getIMIdata(localState.frozenData), isEqual);
        return { immigrationInfoToUpdate: diff ? diff : null };
    } else {
        return { immigrationInfoToUpdate: null };
    }
};

export const mixValue = (values, value) => {
    if(value === null) return values;

    const { applicantGuid, ssn } = value;

    return {
        ...values,
        ...{ [ applicantGuid ]: ssn }
    };
};

export const getHash = (data, ...keys) => {
    const aggKey = keys.map(key => key ?? 0).join('##');
    return CryptoJS.HmacSHA256(data, aggKey).toString(CryptoJS.enc.Base64);
};

// TODO: we enable getRequestBody function instead of getBody in Form component when the function is covered with unit tests
export const getRequestBody = params => {

    const {
        bloodRelationshipStructure,
        bypassedData,
        cachedData,
        data,
        dataSnapshot,
        dataToUpdate,
        destinationPage,
        impersonation,
        inlineCurrentIncomeEnabled,
        invokeInlineEligibility,
        isNonStandaloneInputChanged,
        isSogiFeatureEnabled,
        localState,
        maxAchievedPage,
        medicaidTerminationFeatureEnabled,
        page,
        pageName,
        serverDate,
        ssapApplicationId,
        taxHouseholdStructure
    } = params;

    const dataToSave = updateImiValues(
        updateSsnValues(
            bypassedData ? bypassedData : data,
            mixValue(cachedData.ssns, dataToUpdate.ssnToUpdate)
        ),
        cachedData.immigrationInfo
    );

    const resolvedData = updateDirtyFlags(
        dataToSave,
        dataSnapshot,
        cachedData
    );

    const areDiffsInHhms = !isEqual(
        getApplicantGuids(resolvedData),
        getApplicantGuids(localState.frozenData),
    );

    const {
        shouldRestrictNavigation,
        cleanedData
    } = performCleanUpAndChecks({
        dataAfterChanges: resolvedData,
        dataBeforeChanges: localState.frozenData,
        isSogiFeatureEnabled,
        medicaidTerminationFeatureEnabled,
        pageName,
        serverDate,
    });

    const inconsistencyCriteria = [
        areDiffsInHhms,
        isNonStandaloneInputChanged,
        shouldRestrictNavigation
    ];

    const isDataPotentiallyInconsistent = inconsistencyCriteria.some(criteria => criteria);

    return {
        application: JSON.stringify(
            processUiProperties({
                ...processJsonTools.convertTaxHousehold(
                    processJsonTools.convertBloodRelationships(
                        maskPII(cleanedData),
                        bloodRelationshipStructure
                    ),
                    taxHouseholdStructure
                ),
                ...getCurrentAndMaxAchievedPages(
                    destinationPage,
                    page,
                    maxAchievedPage,
                    isDataPotentiallyInconsistent,
                )
            })
        ),
        applicationId: ssapApplicationId,
        csrOverride: impersonation,
        recalculateIncome: !!bypassedData,
        invokeInlineEligibility: inlineCurrentIncomeEnabled ? invokeInlineEligibility : false,
        ...dataToUpdate
    };
};